import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { Worker, Viewer } from '@react-pdf-viewer/core';
import { anonymizeFilePIIWithRectangles } from '../../../../services/piiService';
import '@react-pdf-viewer/core/lib/styles/index.css';

const uid = () => Math.random().toString(36).slice(2);
const clamp01 = (v) => Math.min(1, Math.max(0, v));

function findPageLayerFromPoint(wrapperEl, canvasEl, clientX, clientY) {
    if (!wrapperEl) return null;

    const els = document.elementsFromPoint(clientX, clientY);
    for (const el of els) {
        if (canvasEl && (el === canvasEl || canvasEl.contains(el))) continue;

        let cur = el;
        while (cur && cur !== wrapperEl) {
            if (cur.classList?.contains('rpv-core__page-layer')) return cur;
            cur = cur.parentElement;
        }
    }
    return null;
}

// ✅ IMPORTANT: react-pdf-viewer virtualizes pages, so DOM order != real page index.
function getActualPageIndexFromLayer(pageLayerEl) {
    if (!pageLayerEl) return null;

    const ds = pageLayerEl.dataset || {};
    if (ds.pageIndex != null) {
        const n = parseInt(ds.pageIndex, 10);
        if (!Number.isNaN(n)) return n;
    }
    if (ds.pageNumber != null) {
        const n = parseInt(ds.pageNumber, 10);
        if (!Number.isNaN(n)) return n - 1;
    }

    const pageNumberAttr =
        pageLayerEl.getAttribute('data-page-number') ||
        pageLayerEl.closest('[data-page-number]')?.getAttribute('data-page-number');
    if (pageNumberAttr != null) {
        const n = parseInt(pageNumberAttr, 10);
        if (!Number.isNaN(n)) return n - 1;
    }

    const testId =
        pageLayerEl.getAttribute('data-testid') ||
        pageLayerEl.closest('[data-testid]')?.getAttribute('data-testid') ||
        '';
    const m1 = testId.match(/(\d+)\s*$/);
    if (m1) return parseInt(m1[1], 10);

    const rp = pageLayerEl.closest('[data-rp^="page-"]')?.getAttribute('data-rp') || '';
    const m2 = rp.match(/^page-(\d+)$/);
    if (m2) return parseInt(m2[1], 10) - 1;

    return null;
}

function isScrollable(el) {
    if (!el) return false;
    const s = window.getComputedStyle(el);
    const oy = s.overflowY;
    const ox = s.overflowX;
    const canY = (oy === 'auto' || oy === 'scroll') && el.scrollHeight > el.clientHeight + 1;
    const canX = (ox === 'auto' || ox === 'scroll') && el.scrollWidth > el.clientWidth + 1;
    return canY || canX;
}

function findFirstScrollableDescendant(root) {
    if (!root) return null;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT);
    let node = walker.currentNode;
    while (node) {
        if (isScrollable(node)) return node;
        node = walker.nextNode();
    }
    return null;
}

export default function PdfPreview({ fileBlob, onRedactionsChange }) {
    const [fileData, setFileData] = useState(null);

    // ✅ Base PDF for the NEXT anonymization (gets replaced by backend output)
    const [currentPdfBlob, setCurrentPdfBlob] = useState(null);

    // ✅ Download
    const [downloadUrl, setDownloadUrl] = useState(null);

    const wrapperRef = useRef(null);
    const canvasRef = useRef(null);

    const [drawMode, setDrawMode] = useState(true);
    const [rects, setRects] = useState([]);
    const [draft, setDraft] = useState(null);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);

    // ---- keep latest state in refs to avoid stale redraw handlers ----
    const drawModeRef = useRef(drawMode);
    const rectsRef = useRef(rects);
    const draftRef = useRef(draft);

    useEffect(() => {
        drawModeRef.current = drawMode;
    }, [drawMode]);

    useEffect(() => {
        rectsRef.current = rects;
        onRedactionsChange?.(rects);
    }, [rects, onRedactionsChange]);

    useEffect(() => {
        draftRef.current = draft;
    }, [draft]);

    // ✅ When input file changes, reset base PDF
    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            if (!fileBlob) {
                setCurrentPdfBlob(null);
                setFileData(null);
                setRects([]);
                setDraft(null);
                setSubmitError(null);
                return;
            }
            if (!cancelled) setCurrentPdfBlob(fileBlob);
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [fileBlob]);

    // ✅ Whenever base PDF changes, rebuild Viewer bytes + download URL
    useEffect(() => {
        let cancelled = false;

        const build = async () => {
            if (!currentPdfBlob) {
                setFileData(null);
                setDownloadUrl((u) => {
                    if (u) URL.revokeObjectURL(u);
                    return null;
                });
                return;
            }

            const buf = await currentPdfBlob.arrayBuffer();
            if (cancelled) return;

            setFileData(new Uint8Array(buf));

            setDownloadUrl((prev) => {
                if (prev) URL.revokeObjectURL(prev);
                return URL.createObjectURL(currentPdfBlob);
            });
        };

        build();
        return () => {
            cancelled = true;
        };
    }, [currentPdfBlob]);

    useEffect(() => {
        return () => {
            setDownloadUrl((u) => {
                if (u) URL.revokeObjectURL(u);
                return null;
            });
        };
    }, []);

    // ---- redraw ----
    const redraw = () => {
        const wrapper = wrapperRef.current;
        const canvas = canvasRef.current;
        if (!wrapper || !canvas) return;

        const wRect = wrapper.getBoundingClientRect();
        const wCss = Math.max(1, Math.floor(wRect.width));
        const hCss = Math.max(1, Math.floor(wRect.height));

        const dpr = window.devicePixelRatio || 1;
        canvas.style.width = `${wCss}px`;
        canvas.style.height = `${hCss}px`;
        canvas.width = Math.floor(wCss * dpr);
        canvas.height = Math.floor(hCss * dpr);

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // reset state
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.globalCompositeOperation = 'source-over';
        ctx.globalAlpha = 1;
        ctx.setLineDash([]);
        ctx.clearRect(0, 0, wCss, hCss);

        const dm = !!drawModeRef.current;
        const rs = rectsRef.current || [];
        const dr = draftRef.current;

        // ✅ RED OVERLAY + ✅ DASHED BORDER AROUND THE WHOLE OVERLAY (what you meant)
        if (dm) {
            ctx.fillStyle = 'rgba(255, 0, 0, 0.06)';
            ctx.fillRect(0, 0, wCss, hCss);

            // bright red dashed border around the overlay area
            ctx.save();
            ctx.strokeStyle = '#ff0000';
            ctx.lineWidth = 4;
            ctx.setLineDash([14, 10]); // --- ---
            ctx.lineCap = 'butt';

            // keep border inside canvas so it's not clipped
            const inset = 3;
            ctx.strokeRect(inset, inset, wCss - inset * 2, hCss - inset * 2);
            ctx.restore();
        }

        const pageLayers = Array.from(wrapper.querySelectorAll('.rpv-core__page-layer'));
        const pageRectByIndex = new Map();
        for (const el of pageLayers) {
            const idx = getActualPageIndexFromLayer(el);
            if (idx != null) pageRectByIndex.set(idx, el.getBoundingClientRect());
        }

        const drawOne = (r, isDraft) => {
            const pr = pageRectByIndex.get(r.pageIndex);
            if (!pr) return;

            const left = pr.left - wRect.left + r.x * pr.width;
            const top = pr.top - wRect.top + r.y * pr.height;
            const width = r.w * pr.width;
            const height = r.h * pr.height;

            // fill rectangles
            ctx.fillStyle = isDraft ? 'rgba(255,0,0,0.30)' : 'rgba(255,0,0,0.18)';
            ctx.fillRect(left, top, width, height);

            // solid red border for rectangles (keep it simple)
            ctx.save();
            ctx.strokeStyle = '#ff0000';
            ctx.lineWidth = 2;
            ctx.setLineDash([]); // solid
            ctx.strokeRect(left, top, width, height);
            ctx.restore();
        };

        rs.forEach((r) => drawOne(r, false));

        if (dm && dr) {
            const x = Math.min(dr.x0, dr.x1);
            const y = Math.min(dr.y0, dr.y1);
            const w = Math.abs(dr.x1 - dr.x0);
            const h = Math.abs(dr.y1 - dr.y0);
            drawOne({ pageIndex: dr.pageIndex, x, y, w, h }, true);
        }
    };

    const rafRef = useRef(0);
    const scheduleRedraw = () => {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = requestAnimationFrame(redraw);
    };

    useEffect(() => {
        scheduleRedraw();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fileData, drawMode, rects, draft]);

    useEffect(() => {
        const wrapper = wrapperRef.current;
        if (!wrapper) return;

        let scroller = null;

        const bind = () => {
            scroller = findFirstScrollableDescendant(wrapper) || wrapper;
            scroller.addEventListener('scroll', scheduleRedraw, { passive: true });
            window.addEventListener('resize', scheduleRedraw, { passive: true });
            scheduleRedraw();
        };

        const unbind = () => {
            if (scroller) scroller.removeEventListener('scroll', scheduleRedraw);
            window.removeEventListener('resize', scheduleRedraw);
        };

        const t1 = setTimeout(bind, 50);
        const t2 = setTimeout(() => {
            unbind();
            bind();
        }, 300);
        const t3 = setTimeout(() => {
            unbind();
            bind();
        }, 900);

        return () => {
            clearTimeout(t1);
            clearTimeout(t2);
            clearTimeout(t3);
            unbind();
            cancelAnimationFrame(rafRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fileData]);

    const pointToPageFrac = (pageEl, clientX, clientY) => {
        const pr = pageEl.getBoundingClientRect();
        return {
            x: clamp01((clientX - pr.left) / pr.width),
            y: clamp01((clientY - pr.top) / pr.height),
        };
    };

    const onPointerDown = (e) => {
        if (!drawMode || isSubmitting) return;
        if (e.button !== undefined && e.button !== 0) return;

        const wrapper = wrapperRef.current;
        const canvas = canvasRef.current;
        const pageEl = findPageLayerFromPoint(wrapper, canvas, e.clientX, e.clientY);
        if (!pageEl) return;

        e.preventDefault();
        canvas?.setPointerCapture?.(e.pointerId);

        const pageIndex = getActualPageIndexFromLayer(pageEl);
        if (pageIndex == null || pageIndex < 0) return;

        const p = pointToPageFrac(pageEl, e.clientX, e.clientY);
        setDraft({ pageIndex, pageEl, x0: p.x, y0: p.y, x1: p.x, y1: p.y });
    };

    const onPointerMove = (e) => {
        if (!drawMode || isSubmitting || !draft) return;
        e.preventDefault();
        const p = pointToPageFrac(draft.pageEl, e.clientX, e.clientY);
        setDraft((d) => (d ? { ...d, x1: p.x, y1: p.y } : d));
    };

    const onPointerUp = (e) => {
        if (!drawMode || isSubmitting || !draft) return;
        e.preventDefault();

        const x = Math.min(draft.x0, draft.x1);
        const y = Math.min(draft.y0, draft.y1);
        const w = Math.abs(draft.x1 - draft.x0);
        const h = Math.abs(draft.y1 - draft.y0);

        const pageIndex = draft.pageIndex;
        setDraft(null);

        if (w < 0.005 || h < 0.005) return;

        const rect = { id: uid(), pageIndex, unit: 'fraction', origin: 'top-left', x, y, w, h };
        setRects((prev) => [...prev, rect]);
    };

    const undo = () => setRects((p) => p.slice(0, -1));
    const clearAll = () => setRects([]);

    const handleApplyRedactions = async () => {
        if (!currentPdfBlob || !rects.length) return;

        setIsSubmitting(true);
        setSubmitError(null);

        try {
            const file = new File([currentPdfBlob], 'manual-redaction.pdf', {
                type: currentPdfBlob.type || 'application/pdf',
            });

            const res = await anonymizeFilePIIWithRectangles(file, rects);

            if (!res?.success) {
                setSubmitError(res?.error || 'Failed to anonymize with rectangles.');
                return;
            }

            const outBlob = res.data?.blob;
            setCurrentPdfBlob(outBlob);

            setRects([]);
            setDraft(null);
        } catch (err) {
            setSubmitError(err?.message || 'Unexpected error.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!fileData) {
        return (
            <Box 
                sx={{ 
                    p: 4, 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    height: '100%',
                    bgcolor: 'action.selected',
                    border: '1px dashed',
                    borderColor: 'divider',
                    borderRadius: 2
                }}
            >
                <Typography variant="body2" color="text.secondary">
                    Náhľad nie je k dispozícii.
                </Typography>
            </Box>
        );
    }

    const hasAnonymizedPdf = !!downloadUrl && !!currentPdfBlob;

    return (
        <Box sx={{ height: '80vh', display: 'flex', flexDirection: 'column', bgcolor: 'background.paper', borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
            {/* --- TOP TOOLBAR --- */}
            <Stack
                direction="row"
                spacing={1.5}
                useFlexGap
                sx={{
                    p: { xs: 1.5, sm: 2 },
                    bgcolor: 'background.default',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                }}
            >
                <Button
                    size="small"
                    variant={drawMode ? 'contained' : 'outlined'}
                    color={drawMode ? 'error' : 'primary'}
                    onClick={() => setDrawMode((v) => !v)}
                    disabled={isSubmitting}
                    disableElevation
                    sx={{ borderRadius: 2, fontWeight: 600 }}
                >
                    {drawMode ? 'Kreslenie: ZAP' : 'Kreslenie: VYP'}
                </Button>

                <Button 
                    size="small" 
                    variant="outlined" 
                    color="inherit"
                    onClick={undo} 
                    disabled={!rects.length || isSubmitting}
                    sx={{ borderRadius: 2, fontWeight: 600, color: 'text.secondary', borderColor: 'divider' }}
                >
                    Späť
                </Button>

                <Button 
                    size="small" 
                    variant="outlined" 
                    color="inherit"
                    onClick={clearAll} 
                    disabled={!rects.length || isSubmitting}
                    sx={{ borderRadius: 2, fontWeight: 600, color: 'text.secondary', borderColor: 'divider' }}
                >
                    Vymazať
                </Button>

                <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    onClick={handleApplyRedactions}
                    disabled={!rects.length || isSubmitting}
                    disableElevation
                    sx={{ borderRadius: 2, fontWeight: 600 }}
                >
                    {isSubmitting ? (
                        <>
                            <CircularProgress size={16} sx={{ mr: 1, color: 'inherit' }} />
                            Aplikujem…
                        </>
                    ) : (
                        'Aplikovať anonymizáciu'
                    )}
                </Button>

                <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    disabled={!hasAnonymizedPdf || isSubmitting}
                    component="a"
                    href={downloadUrl || undefined}
                    download="anonymized.pdf"
                    sx={{ borderRadius: 2, fontWeight: 600 }}
                >
                    Stiahnuť PDF
                </Button>

                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, ml: 'auto !important' }}>
                    Obdĺžniky: {rects.length}
                </Typography>
            </Stack>

            {submitError && (
                <Alert severity="error" sx={{ mx: 2, mt: 2, borderRadius: 2 }}>
                    {String(submitError)}
                </Alert>
            )}

            {/* --- PDF VIEWER AREA --- */}
            <Box sx={{ flex: 1, minHeight: 0, position: 'relative', bgcolor: 'action.selected' }}>
                <Box ref={wrapperRef} sx={{ height: '100%', position: 'relative' }}>
                    
                    {/* Floating Draw Mode Warning */}
                    {drawMode && !isSubmitting && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 12,
                                left: 12,
                                zIndex: 10000,
                                pointerEvents: 'none',
                            }}
                        >
                            <Alert 
                                severity="warning" 
                                icon={false}
                                sx={{ 
                                    py: 0.5, 
                                    px: 1.5, 
                                    borderRadius: 2, 
                                    boxShadow: 2,
                                    border: '1px solid',
                                    borderColor: 'warning.light',
                                    bgcolor: 'background.paper'
                                }}
                            >
                                <Typography variant="caption" sx={{ fontSize: 12, fontWeight: 500, lineHeight: 1.3 }}>
                                    Režim kreslenia je ZAPNUTÝ - posúvanie nefunguje.<br/>
                                    Pre posun v dokumente ho najskôr vypnite.
                                </Typography>
                            </Alert>
                        </Box>
                    )}

                    <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js">
                        <Viewer fileUrl={fileData} />
                    </Worker>

                    <canvas
                        ref={canvasRef}
                        onPointerDown={onPointerDown}
                        onPointerMove={onPointerMove}
                        onPointerUp={onPointerUp}
                        style={{
                            position: 'absolute',
                            inset: 0,
                            zIndex: 9999,
                            pointerEvents: drawMode && !isSubmitting ? 'auto' : 'none',
                            touchAction: drawMode && !isSubmitting ? 'none' : 'auto',
                            cursor: drawMode ? 'crosshair' : 'default',
                        }}
                    />
                </Box>
            </Box>
        </Box>
    );
}